import {Possessed} from "../Possessed";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {PossessedGameAssets} from "../PossessedGameAssets";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import { gsap, TweenMax } from 'gsap';
import { SpineGSAPWrapper } from "../animation/SpineGSAPWrapper";
import {TimelineSprite} from '@nolimitcity/promo-panel/bin/utils/TimelineSprite';
import {CustomEase} from 'gsap/dist/CustomEase';
import { AnimatedMeshRope } from "../components/AnimatedMeshRope";

export class PossessedCrucifixReelView extends PIXI.Container implements StageLayer {

    //private symbolSprites:PIXI.Sprite[] = [];
    //private symbolsView:PIXI.Container;
    
    // Level 5 is all symbols active in the crucifix reel and the final level is 1
    private level:number = 5;
    public getCurrentLevel():number { return this.level; }

    private spineLandscape:PIXI.spine.Spine;
    private spinePortrait:PIXI.spine.Spine;

    private fireBallLandscape:AnimatedMeshRope|undefined;
    private fireBallPortrait:AnimatedMeshRope|undefined;

    constructor() {
        super();
        this.name = "CrucifixReel";
        
        this.visible = false;
        
        this.spineLandscape = new PIXI.spine.Spine(Possessed.assetLoader.getSpineAsset(PossessedGameAssets.CRUCIFIX_SYMBOL_SPINE));
        this.addChild(this.spineLandscape);
        
        this.spinePortrait = new PIXI.spine.Spine(Possessed.assetLoader.getSpineAsset(PossessedGameAssets.CRUCIFIX_SYMBOL_SPINE));
        this.spinePortrait.position.set(0, 33);
        this.addChild(this.spinePortrait);

        this.onResize();
    }

    private getLevelPositionLandscape(level:number):PIXI.Point {
        const reelSymbolSpacing = 88;
        return new PIXI.Point(0, (-3 * reelSymbolSpacing) + reelSymbolSpacing * level);
    }
    private getLevelPositionPortrait(level:number):PIXI.Point {
        const reelSymbolSpacing = 88;
        return new PIXI.Point((-3 * reelSymbolSpacing) + reelSymbolSpacing * level, 0);
    }
    private getPathData(toX: number, toY: number, fromX?: number, fromY?: number): PIXI.IPointData[] {
        if (!fromX) {
            fromX = 0;
        }
        if (!fromY) {
            fromY = 0;
        }
        return [{x: fromX, y: fromY}, {x: toX, y: fromY}, {x: toX, y: toY * 0.8}, {x: toX, y: toY}];
    }

    // Shows the crucifix reel with no fade in
    public show(level:number):gsap.core.Timeline {
        this.level = level;
        const animation = "uiLevel" + this.level;
        const tl =  gsap.timeline()
            .add(()=>{
                this.onResize();
                SpineGSAPWrapper.resetSpineState(this.spineLandscape);
                SpineGSAPWrapper.resetSpineState(this.spinePortrait);
                this.onResize();
                this.visible = true;
                this.spinePortrait.state.setAnimation(0, animation + "_PS", true);
                this.spineLandscape.state.setAnimation(0, animation + "_LS", true);
            })
        return tl;
    }

    public fadeIn(activeSymbols:number = 5):gsap.core.Timeline {
        this.level = activeSymbols;
        const tl =  gsap.timeline()
            .add(()=>{
                SpineGSAPWrapper.resetSpineState(this.spineLandscape);
                SpineGSAPWrapper.resetSpineState(this.spinePortrait);
                this.onResize();
                this.visible = true;
            })
            .add(SpineGSAPWrapper.createTimeline(this.spinePortrait, 0, "uiFadein_P"))
            .add(SpineGSAPWrapper.createTimeline(this.spineLandscape, 0, "uiFadein_L"), "<")
            .add(() => {
                Possessed.soundPlayer.playSound("xcruActivate");
            }, "<")
            .add(() => {
                this.spinePortrait.state.setAnimation(0, "uiFadein_Ps", true);
                this.spineLandscape.state.setAnimation(0, "uiFadein_Ls", true);
            })
        return tl;
    }

    public fadeOut():gsap.core.Timeline {
        return gsap.timeline()
            .add(gsap.to(this, {alpha: 0, duration: 0.5}))
            .add(() => {
                Possessed.soundPlayer.playSound("xcruEnd");
            }, "<")
            .add(() => {
                this.visible = false;
                this.alpha = 1;
                this.spinePortrait.state.setEmptyAnimation(0,0);
                this.spineLandscape.state.setEmptyAnimation(0,0);
                this.level = 5;
            })
    }

    public increaseLevel():gsap.core.Timeline {
        this.level = Math.max(1, this.level-1);
        const animation = "uiLevel" + this.level;
        return gsap.timeline()
            .add(() => {
                this.onResize();         
            })
            .add(SpineGSAPWrapper.createTimeline(this.spinePortrait, 0, animation + "_P"))
            .add(SpineGSAPWrapper.createTimeline(this.spineLandscape, 0, animation + "_L"), "<")
            .add(() => {
                let highlightSound = "xcruHighlight" + (5-this.level);
                if(highlightSound == "xcruHighlight1") {
                    highlightSound = "xcruHighlight";
                }
                Possessed.soundPlayer.playSound(highlightSound)
            }, "<")
            .add(() => {
                const idleAnim = "uiLevel" + this.level;
                this.spinePortrait.state.setAnimation(0, animation + "_PS", true);
                this.spineLandscape.state.setAnimation(0, animation + "_LS", true);
            })
    }

    public shootFireball(startPos:PIXI.Point, targetLevel:number):gsap.core.Timeline {
        let start = startPos;
        start.x -= this.position.x;
        start.y -= this.position.y;

        const targetPosLandscape = this.getLevelPositionLandscape(targetLevel);
        const targetPosPortrait = this.getLevelPositionPortrait(targetLevel);

        const pointsDataLandscape = this.getPathData(targetPosLandscape.x, targetPosLandscape.y, start.x, start.y);
        const pointsDataPortrait = this.getPathData(targetPosPortrait.x, targetPosPortrait.y, start.x, start.y);

        this.fireBallLandscape = new AnimatedMeshRope(Possessed.assetLoader.getTextures("fs_win_swoosh/fs_win_swoosh_"), pointsDataLandscape, 19);
        this.fireBallLandscape.blendMode = PIXI.BLEND_MODES.SCREEN;
        this.fireBallLandscape.visible = false;
        this.addChild(this.fireBallLandscape);

        this.fireBallPortrait = new AnimatedMeshRope(Possessed.assetLoader.getTextures("fs_win_swoosh/fs_win_swoosh_"), pointsDataPortrait, 19);
        this.fireBallPortrait.blendMode = PIXI.BLEND_MODES.SCREEN;
        this.fireBallPortrait.visible = false;
        this.addChild(this.fireBallPortrait);

        const tl = gsap.timeline();
        tl.add(() => {
            if(this.fireBallLandscape)
                this.fireBallLandscape.visible = true;
            if(this.fireBallPortrait)
                this.fireBallPortrait.visible = true;
            this.onResize();
        })
        tl.add(this.fireBallLandscape.playAnimation());
        tl.add(this.fireBallPortrait.playAnimation(), "<");
        tl.add(() => {
            if(this.fireBallLandscape)
                this.removeChild(this.fireBallLandscape);
            if(this.fireBallPortrait)
                this.removeChild(this.fireBallPortrait);    
        })
        return tl;
    }

    onResize(): void {
        const pos:PIXI.Point = NolimitApplication.isLandscape ? new PIXI.Point(-80, 420) : new PIXI.Point(360, -25);
        this.position.copyFrom(pos);
        
        if(NolimitApplication.isLandscape) {
            this.spineLandscape.alpha = 1;
            this.spinePortrait.alpha = 0;

            if(this.fireBallLandscape) this.fireBallLandscape.alpha = 1;
            if(this.fireBallPortrait) this.fireBallPortrait.alpha = 0;
        }
        else {
            this.spineLandscape.alpha = 0;
            this.spinePortrait.alpha = 1;

            if(this.fireBallLandscape) this.fireBallLandscape.alpha = 0;
            if(this.fireBallPortrait) this.fireBallPortrait.alpha = 1;
        }
    }

    public onRestore() {
        if(Possessed.model.isRespin() || (Possessed.model.initData.xCrucifixReel?.length && !Possessed.model.triggeredFreespins())) {
            this.level = Possessed.model.initData.xCrucifixReel.length;
            this.visible = true;
            if(this.level == 5) {
                const tl = this.fadeIn(this.level);
                tl.progress(1);
            } else {
                this.show(this.level);
            }
        }
    }
}